import { FormEvent, useEffect, useState } from 'react';
import TerrainMapPreview from '../components/TerrainMapPreview/TerrainMapPreview';
import MapSelectModal from '../components/MapSelectModal/MapSelectModal';
import CommanderSelectModal, {
  CommanderEntry,
  FactionEntry,
} from '../components/CommanderSelectModal/CommanderSelectModal';
import { useTeaForm } from '../util/tea-form';
import { get, post, useAuth } from '../api';
import FindUser from '../components/FindUser/FindUser';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import './setup-game.page.css';

export interface MapListEntry {
  id: string;
  name: string;
  description: string;
  playerCount: number;
  terrainMap: {
    width: number;
    height: number;
    data: { [key: string]: string };
  };
  ownerOverrides?: { [key: string]: number };
  playerSlots: { startingFunds: number }[];
}

const AI_PRESETS = [
  { value: 'adaptive', label: 'Adaptive' },
  { value: 'positional', label: 'Positional' },
  { value: 'tempo', label: 'Tempo' },
] as const;

function canonicalAiPreset(value?: string): string {
  switch (value) {
    case 'balanced':
      return 'adaptive';
    case 'defensive':
      return 'positional';
    case 'aggressive':
    case 'rusher':
      return 'tempo';
    default:
      return value ?? 'adaptive';
  }
}

const PLAYER_COLORS = ['#68b8e8', '#d43318', '#f7b728', '#5db854'];

// The faction-matched default commander per slot color (blue -> Ferra,
// red -> Rukh, yellow -> Aldous, green -> Lyra), matching wars-engine's
// factionDefaultCommander ordering. Every filled slot gets one of these so a
// game always has commanders by default; the player can clear it explicitly.
const DEFAULT_COMMANDER_BY_SLOT = ['ferra', 'rukh', 'aldous', 'lyra'];

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

interface PlayerSetup {
  type: 'human' | 'ai' | 'none';
  userId?: string;
  aiPersonality?: string;
  startingFunds?: number;
  commanderId?: string;
}

interface GameSetupForm {
  mapId: string;
  players: PlayerSetup[];
  fogOfWar: boolean;
}

const PlayerSetupSchema = z.object({
  type: z.enum(['human', 'ai', 'none']),
  userId: z.string().optional(),
  aiPersonality: z.string().optional(),
  startingFunds: z.number().optional(),
  commanderId: z.string().optional(),
});

const GameSetupSchema = z.object({
  mapId: z.string().min(1, 'Select a map'),
  fogOfWar: z.boolean(),
  players: z
    .array(PlayerSetupSchema)
    .min(1)
    .superRefine((players, ctx) => {
      const filledSlots = players.filter((p) => p.type !== 'none');
      if (filledSlots.length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least 2 slots must be filled (Human or AI).',
          path: ['players'],
        });
      }
      const humanSlots = players.filter((p) => p.type === 'human');
      humanSlots.forEach((p, i) => {
        if (!p.userId) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Human players must have a name assigned',
            path: [i, 'userId'],
          });
        }
      });
    }),
});

function defaultPlayerSetup(
  index: number,
  currentUserSub?: string
): PlayerSetup {
  const commanderId =
    DEFAULT_COMMANDER_BY_SLOT[index % DEFAULT_COMMANDER_BY_SLOT.length];
  if (index === 0 && currentUserSub) {
    return { type: 'human', userId: currentUserSub, commanderId };
  }
  if (index === 1) {
    return { type: 'ai', aiPersonality: 'adaptive', commanderId };
  }
  return { type: 'none' };
}

function resizePlayers(
  players: PlayerSetup[],
  count: number,
  currentUserSub?: string
): PlayerSetup[] {
  if (players.length === count) {
    return players;
  }
  if (players.length > count) {
    return players.slice(0, count);
  }
  const extra = Array.from({ length: count - players.length }, (_, i) =>
    defaultPlayerSetup(players.length + i, currentUserSub)
  );
  return [...players, ...extra];
}

function selectedMap(
  mapId: string,
  maps: MapListEntry[]
): MapListEntry | undefined {
  return maps.find((m) => m.id === mapId);
}

export default function SetupGamePage() {
  const auth = useAuth();
  const sub = auth?.sub;
  const isGuest = !!auth?.isGuest;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectMapId = searchParams.get('mapId');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [maps, setMaps] = useState<MapListEntry[]>([]);
  const [showMapModal, setShowMapModal] = useState(false);
  const [commanders, setCommanders] = useState<CommanderEntry[]>([]);
  const [factions, setFactions] = useState<Record<string, FactionEntry>>({});
  const [commanderSlot, setCommanderSlot] = useState<number | null>(null);

  const initialValue: GameSetupForm = {
    mapId: '',
    players: [{ type: 'human', userId: sub ?? undefined }],
    // Fog on by default — matches the server default for omitted values.
    fogOfWar: true,
  };

  const {
    value: setup,
    errors,
    setValue,
  } = useTeaForm({
    initialValue,
    schema: GameSetupSchema,
  });

  useEffect(() => {
    get('maps')
      .then(setMaps)
      .catch(() => {});
  }, []);

  useEffect(() => {
    get('commanders')
      .then((res) => {
        setCommanders(res?.commanders ?? []);
        setFactions(res?.factions ?? {});
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (setup.mapId || maps.length === 0) {
      return;
    }
    const preselected = preselectMapId
      ? maps.find((m) => m.id === preselectMapId)
      : undefined;
    const target = preselected ?? maps[0];
    setValue((current: GameSetupForm) => ({
      ...current,
      mapId: target.id,
      players: resizePlayers(current.players, target.playerCount, sub),
    }));
  }, [maps, setup.mapId, sub, setValue, preselectMapId]);

  const selectMap = (map: MapListEntry) => {
    setValue((current: GameSetupForm) => ({
      ...current,
      mapId: map.id,
      players: resizePlayers(current.players, map.playerCount, sub),
    }));
    setShowMapModal(false);
  };

  const handlePlayerChange = (
    index: number,
    field: keyof PlayerSetup,
    value: string | number | undefined
  ) => {
    setValue((current: GameSetupForm) => {
      const players = [...current.players];
      const currentPlayer = { ...players[index] };

      if (field === 'type' && value === 'ai') {
        currentPlayer.type = 'ai';
        currentPlayer.userId = undefined;
        currentPlayer.aiPersonality = currentPlayer.aiPersonality ?? 'adaptive';
        currentPlayer.commanderId =
          currentPlayer.commanderId ??
          DEFAULT_COMMANDER_BY_SLOT[index % DEFAULT_COMMANDER_BY_SLOT.length];
      } else if (field === 'type' && value === 'human') {
        currentPlayer.type = 'human';
        currentPlayer.aiPersonality = undefined;
        currentPlayer.commanderId =
          currentPlayer.commanderId ??
          DEFAULT_COMMANDER_BY_SLOT[index % DEFAULT_COMMANDER_BY_SLOT.length];
      } else if (field === 'type' && value === 'none') {
        currentPlayer.type = 'none';
        currentPlayer.userId = undefined;
        currentPlayer.aiPersonality = undefined;
        currentPlayer.startingFunds = undefined;
        currentPlayer.commanderId = undefined;
      } else if (field === 'startingFunds') {
        currentPlayer.startingFunds =
          value === '' || value === undefined ? undefined : Number(value);
      } else if (field === 'userId') {
        currentPlayer.userId = value as string | undefined;
      } else if (field === 'aiPersonality') {
        currentPlayer.aiPersonality = value as string | undefined;
      }

      players[index] = currentPlayer;
      return { ...current, players };
    });
  };

  const handleCommanderChange = (
    index: number,
    commanderId: string | undefined
  ) => {
    setValue((current: GameSetupForm) => {
      const players = [...current.players];
      players[index] = { ...players[index], commanderId };
      return { ...current, players };
    });
    setCommanderSlot(null);
  };

  const commanderName = (id?: string): string => {
    if (!id) return 'None';
    const commander = commanders.find((c) => c.id === id);
    return commander ? `${commander.name} — ${commander.title}` : id;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    const payload = {
      mapId: setup.mapId,
      fogOfWar: setup.fogOfWar,
      players: setup.players.map((p: PlayerSetup) => ({
        type: p.type,
        userId: p.type === 'human' ? p.userId : undefined,
        aiPersonality: p.type === 'ai' ? p.aiPersonality : undefined,
        startingFunds: p.startingFunds,
        commanderId: p.commanderId,
      })),
    };

    try {
      const result = await post('games', payload);
      navigate(`/games/${result.id}`);
    } catch (e) {
      setSubmitError('Could not create the game. Please try again.');
    }
  };

  const containsCurrentUser =
    !sub ||
    !setup.players.some((p: PlayerSetup) => p.type === 'human') ||
    setup.players.some(
      (p: PlayerSetup) => p.type === 'human' && p.userId === sub
    );

  const hasValidationErrors = errors.length > 0 || !containsCurrentUser;
  const currentMap = selectedMap(setup.mapId, maps);

  return (
    <div className="setup-page">
      <div className="setup-page__inner">
        <h1 className="title is-3 setup-page__title mb-5">Skirmish Lobby</h1>

        {isGuest && (
          <div className="notification is-warning is-light py-2 px-3 mb-4">
            As a guest you can only play with AI players. Create an account in
            order to play against other players.{' '}
            <Link className="button is-warning is-small ml-2" to="/account">
              Create Account
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit} method="none">
          <div className="columns">
            <div className="column is-7">
              {currentMap && (
                <div className="box setup-panel" style={{ overflow: 'hidden' }}>
                  <div
                    className="is-flex is-justify-content-space-between is-align-items-flex-start mb-4"
                    style={{ minWidth: 0 }}
                  >
                    <div style={{ minWidth: 0, flex: 1, marginRight: 12 }}>
                      <p
                        style={{
                          fontSize: '1.25rem',
                          fontWeight: 600,
                          lineHeight: 1.4,
                          marginBottom: 4,
                          wordBreak: 'break-word',
                        }}
                      >
                        {currentMap.name}
                      </p>
                      <p
                        className="setup-page__map-desc"
                        style={{
                          fontSize: '0.95rem',
                          lineHeight: 1.5,
                          marginBottom: 8,
                          wordBreak: 'break-word',
                        }}
                      >
                        {currentMap.description}
                      </p>
                      <div className="tags mb-0">
                        <span className="tag is-info">
                          {currentMap.playerCount} player
                          {currentMap.playerCount > 1 ? 's' : ''}
                        </span>
                        <span className="tag is-light">
                          {currentMap.terrainMap.width} x{' '}
                          {currentMap.terrainMap.height}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="button is-small is-light"
                      onClick={() => setShowMapModal(true)}
                      style={{ flexShrink: 0 }}
                    >
                      Change Map
                    </button>
                  </div>
                  <TerrainMapPreview
                    terrainMap={currentMap.terrainMap}
                    cellSize={20}
                    ownerOverrides={currentMap.ownerOverrides}
                    playerCount={currentMap.playerCount}
                    highlightOwners
                  />
                </div>
              )}
            </div>

            <div className="column is-5">
              <div className="box setup-panel">
                <p className="title is-5 mb-4">Game Setup</p>

                {setup.players.map((player: PlayerSetup, index: number) => {
                  const slotColor = PLAYER_COLORS[index % PLAYER_COLORS.length];
                  const isEmpty = player.type === 'none';
                  return (
                    <div
                      key={index}
                      className={`mb-4 player-slot${
                        isEmpty ? ' player-slot--empty' : ''
                      }`}
                      style={
                        {
                          '--slot-color': isEmpty ? '#aaa' : slotColor,
                          '--slot-glow': hexToRgba(slotColor, 0.35),
                        } as React.CSSProperties
                      }
                    >
                      <div className="is-flex is-align-items-center mb-2">
                        <span className="player-slot__badge">{index + 1}</span>
                        <div className="select is-small">
                          <select
                            value={player.type}
                            onChange={(e) =>
                              handlePlayerChange(index, 'type', e.target.value)
                            }
                          >
                            <option
                              value="human"
                              disabled={isGuest && player.userId !== sub}
                            >
                              Human
                            </option>
                            <option value="ai">AI</option>
                            <option value="none">Empty</option>
                          </select>
                        </div>
                      </div>

                      {player.type === 'human' && (
                        <div className="field is-horizontal mb-2">
                          <div
                            className="field-label is-small"
                            style={{ flexGrow: 0, width: 50 }}
                          >
                            <label className="label">Name</label>
                          </div>
                          <div className="field-body">
                            <FindUser
                              name={`players[${index}].userId`}
                              value={player.userId}
                              onChange={({ target }: any) =>
                                handlePlayerChange(
                                  index,
                                  'userId',
                                  target.value
                                )
                              }
                              disabled={isGuest}
                            />
                          </div>
                        </div>
                      )}

                      {player.type === 'ai' && (
                        <div className="field is-horizontal mb-2">
                          <div
                            className="field-label is-small"
                            style={{ flexGrow: 0, width: 50 }}
                          >
                            <label className="label">Style</label>
                          </div>
                          <div className="field-body">
                            <div className="select is-small">
                              <select
                                value={canonicalAiPreset(player.aiPersonality)}
                                onChange={(e) =>
                                  handlePlayerChange(
                                    index,
                                    'aiPersonality',
                                    e.target.value
                                  )
                                }
                              >
                                {AI_PRESETS.map((p) => (
                                  <option key={p.value} value={p.value}>
                                    {p.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {player.type !== 'none' && (
                        <>
                          <div className="field is-horizontal mb-0">
                            <div
                              className="field-label is-small"
                              style={{ flexGrow: 0, width: 50 }}
                            >
                              <label className="label">Funds</label>
                            </div>
                            <div className="field-body">
                              <div className="control">
                                <input
                                  className="input is-small"
                                  type="number"
                                  style={{ width: 100 }}
                                  placeholder={
                                    currentMap
                                      ? String(
                                          currentMap.playerSlots[index]
                                            ?.startingFunds ?? ''
                                        )
                                      : ''
                                  }
                                  value={player.startingFunds ?? ''}
                                  onChange={(e) =>
                                    handlePlayerChange(
                                      index,
                                      'startingFunds',
                                      e.target.value
                                    )
                                  }
                                  min={0}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="field is-horizontal mb-0">
                            <div
                              className="field-label is-small"
                              style={{ flexGrow: 0, width: 50 }}
                            >
                              <label className="label">Leader</label>
                            </div>
                            <div className="field-body">
                              <div
                                className="is-flex is-align-items-center"
                                style={{ gap: 8, width: '100%' }}
                              >
                                <span
                                  className="is-size-7"
                                  style={{
                                    flex: 1,
                                    color: 'var(--text-secondary)',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {commanderName(player.commanderId)}
                                </span>
                                <button
                                  type="button"
                                  className="button is-small is-light"
                                  onClick={() => setCommanderSlot(index)}
                                >
                                  Choose
                                </button>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}

                {!containsCurrentUser && (
                  <div className="notification is-warning is-light py-2 px-3 mb-3">
                    Assign yourself to at least one human slot.
                  </div>
                )}

                <label
                  className="checkbox is-flex is-align-items-center mb-4"
                  style={{ gap: 8 }}
                >
                  <input
                    type="checkbox"
                    checked={setup.fogOfWar}
                    onChange={(e) =>
                      setValue((current: GameSetupForm) => ({
                        ...current,
                        fogOfWar: e.target.checked,
                      }))
                    }
                  />
                  <span>
                    Fog of war — hide the map and units outside your units&apos;
                    vision
                  </span>
                </label>

                {submitError && (
                  <div className="notification is-danger is-light py-2 px-3 mb-3">
                    {submitError}
                  </div>
                )}
                {hasValidationErrors && errors.length > 0 && (
                  <div className="notification is-danger is-light py-2 px-3 mb-3">
                    Please fix the errors above before launching.
                  </div>
                )}

                <button
                  className="button is-medium is-fullwidth launch-button"
                  type="submit"
                  disabled={hasValidationErrors}
                >
                  Launch Game
                </button>
              </div>
            </div>
          </div>
        </form>

        {showMapModal && (
          <MapSelectModal
            maps={maps}
            currentMapId={setup.mapId}
            onSelect={selectMap}
            onClose={() => setShowMapModal(false)}
          />
        )}

        {commanderSlot !== null && (
          <CommanderSelectModal
            commanders={commanders}
            factions={factions}
            currentId={setup.players[commanderSlot]?.commanderId}
            onSelect={(id) => handleCommanderChange(commanderSlot, id)}
            onClose={() => setCommanderSlot(null)}
          />
        )}
      </div>
    </div>
  );
}
